/**
 * Roles service — MOCK (CT-5563 shared data layer).
 *
 * System roles (Developer, Viewer) are read-only; custom roles can be created,
 * edited and deleted. Permission grants reference the studios catalog tree.
 * Contract TBD (ARP/backend); platform envelope, snake_case bodies.
 */
import {
  envelope,
  errorEnvelope,
  matchesSearch,
  notFoundEnvelope,
} from '../shared/utils/mockServiceHelpers.js';
import { isNameTaken, memberRefError, membershipApi, withMembers } from './collectionHelpers.js';
import { dropFromAllMembers, setMembership } from './membershipHelpers.js';
import { flattenPermissions, PERMISSION_LEVELS } from './seeds/studios.js';
import { nextId, resetStore, store } from './seeds/store.js';

export const ROLE_TYPES = ['system', 'custom'];
const KEY = 'role_ids';

/** Test-only: reset the shared mock store (members, roles, groups, grants). */
export const __resetMockRolesForTests = () => resetStore();

const view = (role) => withMembers(KEY, role);

const findRole = (id) => store.roles.find((r) => r.id === id);
const systemForbidden = (what) =>
  errorEnvelope(403, 'SYSTEM_ROLE_PROTECTED', `System roles cannot be ${what}.`);
const nameTaken = (name, exceptId) => isNameTaken(store.roles, name, exceptId);

const validatePermissions = (permissions = []) => {
  const known = new Set(
    store.studios.flatMap((s) => flattenPermissions(s.permissions)).map((n) => n.id)
  );
  const bad = permissions.find(
    (g) => !known.has(g.permission_id) || !PERMISSION_LEVELS.includes(g.level)
  );
  return bad ? `Invalid permission grant: ${bad.permission_id}` : null;
};

const validate = (payload, exceptId) => {
  if ('name' in payload) {
    if (!String(payload.name ?? '').trim())
      return errorEnvelope(422, 'NAME_REQUIRED', 'Role name is required.');
    if (nameTaken(payload.name, exceptId))
      return errorEnvelope(409, 'NAME_TAKEN', 'A role with this name already exists.');
  }
  if (payload.permissions) {
    const invalid = validatePermissions(payload.permissions);
    if (invalid) return errorEnvelope(422, 'INVALID_PERMISSION', invalid);
  }
  return memberRefError(payload.member_ids);
};

/**
 * Roles grouped for the Roles page (system first, then custom); `search.query`
 * matches name + description, `filters.type` narrows to system|custom.
 * TODO(CT-5563 / backend): `GET /people-access/roles` — contract TBD (ARP/backend).
 */
export const listRoles = (params = {}) => {
  const { filters = {}, search = {} } = params;
  const items = store.roles
    .filter((r) => !filters.type || r.type === filters.type)
    .filter((r) => matchesSearch(r, search.query, ['name', 'description']))
    .map(view);
  return Promise.resolve(envelope({ items, total: items.length }, { total: items.length }));
};

/**
 * One role with its permission grants.
 * TODO(CT-5563 / backend): `GET /people-access/roles/:id` — contract TBD (ARP/backend).
 */
export const getRole = (id) => {
  const role = findRole(id);
  return Promise.resolve(role ? envelope(view(role)) : notFoundEnvelope('Role not found'));
};

/**
 * Create a custom role (wizard: Details → Permissions → Members).
 * TODO(CT-5563 / backend): `POST /people-access/roles` — contract TBD (ARP/backend).
 */
export const createRole = (payload = {}) => {
  const error = validate({ name: '', ...payload });
  if (error) return Promise.resolve(error);
  const now = new Date().toISOString();
  const role = {
    id: nextId('role'),
    name: payload.name.trim(),
    type: 'custom',
    description: payload.description ?? '',
    studios: [...(payload.studios ?? [])],
    permissions: (payload.permissions ?? []).map(({ permission_id, level }) => ({
      permission_id,
      level,
    })),
    created_at: now,
    updated_at: now,
  };
  store.roles = [...store.roles, role];
  setMembership(KEY, role.id, payload.member_ids ?? []);
  return Promise.resolve(envelope(view(role)));
};

/**
 * Edit a custom role (`member_ids`, when present, replaces the member set).
 * TODO(CT-5563 / backend): `PATCH /people-access/roles/:id` — contract TBD (ARP/backend).
 */
export const updateRole = (id, patch = {}) => {
  const role = findRole(id);
  if (!role) return Promise.resolve(notFoundEnvelope('Role not found'));
  if (role.type === 'system') return Promise.resolve(systemForbidden('edited'));
  const error = validate(patch, id);
  if (error) return Promise.resolve(error);
  const next = { ...role, updated_at: new Date().toISOString() };
  ['name', 'description', 'studios', 'permissions'].forEach((k) => {
    if (patch[k] !== undefined) next[k] = k === 'name' ? patch[k].trim() : patch[k];
  });
  store.roles = store.roles.map((r) => (r.id === id ? next : r));
  if (patch.member_ids) setMembership(KEY, id, patch.member_ids);
  return Promise.resolve(envelope(view(next)));
};

/**
 * Delete a custom role; it is removed from every member that had it.
 * TODO(CT-5563 / backend): `DELETE /people-access/roles/:id` — contract TBD (ARP/backend).
 */
export const deleteRole = (id) => {
  const role = findRole(id);
  if (!role) return Promise.resolve(notFoundEnvelope('Role not found'));
  if (role.type === 'system') return Promise.resolve(systemForbidden('deleted'));
  dropFromAllMembers(KEY, id);
  store.roles = store.roles.filter((r) => r.id !== id);
  return Promise.resolve(envelope({ id, deleted: true }));
};

const roleMembers = membershipApi(KEY, findRole, 'Role not found');

/**
 * Members holding a role (paginated).
 * TODO(CT-5563 / backend): `GET /people-access/roles/:id/members` — contract TBD (ARP/backend).
 */
export const listRoleMembers = roleMembers.list;

/**
 * Assign a role to more members.
 * TODO(CT-5563 / backend): `POST /people-access/roles/:id/members` — contract TBD (ARP/backend).
 */
export const addRoleMembers = roleMembers.add;

/**
 * Take a role away from one member.
 * TODO(CT-5563 / backend): `DELETE /people-access/roles/:id/members/:member_id` — contract TBD (ARP/backend).
 */
export const removeRoleMember = roleMembers.remove;
